import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { env } from '../../../environments/env';
import { MsalService } from '@azure/msal-angular';

export type StompConnectionState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'ERROR';

export interface ChecksheetNotification {
  type: 'CREATED' | 'UPDATED';
  projectId: number;
  assignmentId: number;
  cpId: number;
  mcId?: number | null;
  timestamp: string;
  assignmentStatus?: string;
  // Delta fields — absent means "not changed", "NULL" string means clear
  item?: {
    itemNumber: number;
    status: string;
    enteredBy?: string | null;
    enteredByTimestamp?: string | null;
  };
  signedBy?: string | null;
  signedByTimestamp?: string | null;
  verifiedBy?: string | null;
  verifiedByTimestamp?: string | null;
  comments?: string | null;
}

export interface PunchNotification {
  type: 'CREATED' | 'UPDATED';
  projectId: number;
  punchId: number;
  cpId?: number | null;
  mcpId?: number | null;
  description: string;
  category: string;
  timestamp: string;
  enteredBy: string;
  enteredDate: string;
  clearedBy: string;
  clearedDate: string;
  acceptedBy: string;
  acceptedDate: string;
}

@Injectable({ providedIn: 'root' })
export class StompWebSocketService implements OnDestroy {
  private client: Client | null = null;
  private readonly state$ = new BehaviorSubject<StompConnectionState>('DISCONNECTED');
  private readonly messages$ = new Subject<IMessage>();

  readonly connectionState$: Observable<StompConnectionState> = this.state$.asObservable();
  readonly incomingMessages$: Observable<IMessage> = this.messages$.asObservable();

  private readonly topicDestination = '/topic/notifications';

  // Active subscription objects and the set of desired project subscriptions
  private readonly projectSubs = new Map<number, any>();
  private readonly desiredProjectIds = new Set<number>();

  // Simple auto-reconnect flag
  private readonly autoReconnect = true;

  // Reconnect/backoff state
  private reconnectAttempts = 0;
  private readonly maxFastReconnectAttempts = 2; // deprecated by new backoff, kept for safety
  private readonly reconnectCooldownMs = 60 * 1000; // legacy cooldown
  private reconnectTimer: any;
  private hadConnected = false; // indicates we successfully connected at least once
  private backoffIndex = 0; // index into backoffDelays for initial connection failures
  private readonly backoffDelays = [1000, 6000, 60000, 600000]; // 1s, 6s, 60s, 600s
  // Limited retries for abnormal close (1006)
  private abnormalRetryAttempts = 0;
  private readonly maxAbnormalRetries = 2;
  private readonly abnormalBackoffsMs = [1000, 5000];

  constructor(private readonly msal: MsalService) {}

  private scheduleReconnect(closeCode?: number, immediateHint: boolean = false): void {
    if (!this.autoReconnect) {
      return;
    }
    if (!env.get().featureFlags.stomp) {
      return;
    }

    // Explicitly block reconnects on protocol/policy violation (1002)
    if (closeCode === 1002) {
      console.info('[STOMP] websocket closed (1002 protocol/policy violation) - will not reconnect');
      // Reset state to avoid any immediate retry paths
      this.hadConnected = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      return;
    }

    // Only consider retries if we had a previously established connection, or a caller explicitly hints immediate.
    if (!this.hadConnected && !immediateHint) {
      return;
    }

    // Special case: only retry for abnormal closure (1006), up to maxAbnormalRetries.
    if (closeCode !== 1006 && !immediateHint) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (immediateHint) {
      this.connect().catch(() => {
        // On failure, fall back to abnormal retry path if code indicates 1006 on next close.
        this.hadConnected = false;
      });
      return;
    }

    if (this.abnormalRetryAttempts >= this.maxAbnormalRetries) {
      return;
    }
    const delay = this.abnormalBackoffsMs[Math.min(this.abnormalRetryAttempts, this.abnormalBackoffsMs.length - 1)];
    this.abnormalRetryAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {
        // Attempts are capped; do nothing here.
      });
    }, delay);
  }

  private setDisconnected(reason?: string): void {
    this.state$.next('DISCONNECTED');
    console.info('[STOMP] disconnected', reason ?? '');
  }

  async connect(): Promise<void> {
    if (!env.get().featureFlags.stomp) {
      // Feature disabled in this environment; no-op.
      this.setDisconnected('feature disabled');
      return;
    }
    // No-op if already active or connected.
    if (this.client && (this.client.active || this.client.connected)) {
      return;
    }

    this.state$.next('CONNECTING');

    // Acquire access token (best-effort).
    let accessToken: string | null = null;
    try {
      const accounts = this.msal.instance.getAllAccounts();
      if (accounts.length) {
        const result = await this.msal.instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [env.get().auth.apiScope],
        });
        accessToken = result?.accessToken ?? null;
      }
    } catch {
      accessToken = null;
    }

    try {
      const baseUrl = env.get().api.wsUrl;
      console.info('[STOMP] connecting to:', baseUrl);

      const client = new Client({
        webSocketFactory: () => new SockJS(baseUrl),
        connectHeaders: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        // Heartbeats (ms): 30s send, 30s expect from server to reduce WS traffic
        heartbeatIncoming: 30000,
        heartbeatOutgoing: 30000,
        debug: (str) => console.debug('[STOMP debug]', str),
      });

      const handleTransportClose = (reason: string, code?: number) => {
        console.info('[STOMP]', reason);
        try {
          for (const [pid, sub] of Array.from(this.projectSubs.entries())) {
            try {
              sub.unsubscribe();
            } catch {
              /* ignore */
            }
            this.projectSubs.delete(pid);
          }
        } catch {
          /* ignore */
        }
        try {
          client.deactivate();
        } catch {
          /* ignore */
        }
        this.client = null;
        this.setDisconnected(reason);
        // Only retry a couple of times for abnormal closures (1006). Clean closes won't retry.
        this.scheduleReconnect(code);
      };

      client.onConnect = () => {
        this.state$.next('CONNECTED');
        console.info('[STOMP] connected');
        this.hadConnected = true; // mark that we have achieved a connection
        this.backoffIndex = 0; // reset backoff after success
        this.abnormalRetryAttempts = 0; // reset abnormal retry counter
        try {
          client.subscribe(this.topicDestination, (msg) => this.messages$.next(msg));
          for (const pid of Array.from(this.desiredProjectIds)) {
            try {
              this._doSubscribeProject(pid, client);
            } catch {
              /* ignore */
            }
          }
        } catch {
          // ignore
        }
      };

      client.onStompError = () => handleTransportClose('stomp error - disconnecting');
      client.onWebSocketError = () => handleTransportClose('websocket transport error - disconnecting');
      client.onWebSocketClose = (evt) => {
        const code = evt?.code ?? undefined;
        handleTransportClose(`websocket closed (${code ?? 'unknown'})`, code);
      };

      try {
        client.activate();
        this.client = client;
        this.reconnectAttempts = 0; // legacy field retained
      } catch {
        this.client = null;
        this.setDisconnected('failed to activate client');
        // Do not schedule reconnect if never connected successfully.
      }
    } catch {
      this.client = null;
      this.setDisconnected('unexpected error during connect setup');
      // Do not schedule reconnect if never connected successfully.
    }
  }

  disconnect(): void {
    try {
      if (this.client && this.client.active) {
        this.client.deactivate();
      }
    } catch {
      // ignore
    }
    try {
      for (const [pid, sub] of Array.from(this.projectSubs.entries())) {
        try {
          sub.unsubscribe();
        } catch {
          /* ignore */
        }
        this.projectSubs.delete(pid);
      }
    } catch {
      /* ignore */
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;

    this.client = null;
    this.state$.next('DISCONNECTED');
    console.info('[STOMP] disconnected (manual)');
  }

  sendNotification(payload: string): void {
    if (!this.client || !this.client.connected) {
      return;
    }
    try {
      this.client.publish({ destination: '/app/notify', body: payload });
    } catch {
      // ignore
    }
  }

  subscribe(destination: string, cb: (msg: IMessage) => void): void {
    if (!this.client || !this.client.connected) {
      return;
    }
    try {
      this.client.subscribe(destination, cb);
    } catch {
      // ignore
    }
  }

  subscribeRaw(destination: string, cb: (msg: IMessage) => void): any {
    if (!this.client || !this.client.connected) {
      return null;
    }
    try {
      return this.client.subscribe(destination, cb);
    } catch {
      return null;
    }
  }

  subscribeToProjectPunches(projectId: number): void {
    if (this.projectSubs.has(projectId)) {
      return;
    }
    this.desiredProjectIds.add(projectId);
    if (this.client && this.client.connected) {
      try {
        this._doSubscribeProject(projectId, this.client);
      } catch {
        /* ignore */
      }
    }
  }

  unsubscribeFromProjectPunches(projectId: number): void {
    this.desiredProjectIds.delete(projectId);
    const sub = this.projectSubs.get(projectId);
    if (sub) {
      try {
        sub.unsubscribe();
      } catch {
        // ignore
      }
      this.projectSubs.delete(projectId);
    }
  }

  private _doSubscribeProject(projectId: number, client: Client): void {
    if (this.projectSubs.has(projectId)) {
      return;
    }
    const destination = `/topic/completion.punches.${projectId}`;
    const sub = client.subscribe(destination, (msg) => this.messages$.next(msg));
    this.projectSubs.set(projectId, sub);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
